import { Router } from '@angular/router';
import {
  Component,
  Inject,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  TemplateRef,
  PLATFORM_ID,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Store } from '@ngrx/store';
import { IAppState } from '@app/store';
import * as fromDataLayer from '@app/analytics/actions/data-layer';
import { TrackMerchBlock } from '@app/+catalog/store';

interface RoutingContext {
  commands: any[];
  fragment?: string;
  queryParams?: { [k: string]: any };
}

/**
 * A component to create hyperlinks.
 */
@Component({
  selector: 'vc-ref',
  templateUrl: './ref.component.html',
  styleUrls: ['./ref.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RefComponent implements OnChanges {
  /**
   * A `TemplateRef` to render hyperlink as an internal routerLink.
   */
  @ViewChild('routerRef', { static: true })
  routerRef: TemplateRef<any>;

  /**
   * A `TemplateRef` to render hyperlink as an absolute link.
   */
  @ViewChild('absoluteRef', { static: true })
  absoluteRef: TemplateRef<any>;

  /**
   * A `TemplateRef` for hyperlink obfuscation.
   */
  @ViewChild('anchoredRef', { static: true })
  anchoredRef: TemplateRef<any>;

  /**
   * Default rendering as anchor tag.
   *
   * When option is set to `false`, the component view should be
   * rendered as an obfuscated link.
   */
  @Input()
  anchor = true;

  /**
   * Specifies if linked resource should be opened in a new window or tab.
   * When option is set to `true` a `target="_blank"` attribute is added to anchor tag.
   */
  @Input()
  blank = false;

  /**
   * Specifies if link resource should stop the propagation.
   * When option is set to `true` the stopPropagation function is call.
   */
  @Input()
  stopPropagation = false;

  /**
   * If url has a trailing slash, remove it.
   */
  @Input()
  stripSlash = false;

  /**
   * Active micro data for this link
   */
  @Input()
  microData = false;

  /**
   * Active display block for specific link
   */
  @Input()
  isBlock = false;

  /**
   * Active display flex for specific link
   */
  @Input()
  isFlex = false;

  /**
   * disable link
   */
  @Input()
  disableLink = false;

  /**
   * add ariaLabel attribute
   */
  @Input()
  ariaLabel: string;

  /**
   * add ariaLabelledby attribute
   * when the button contains an image
   */
  @Input()
  ariaLabelledby: string;

  /**
   * add class if needed
   */
  @Input()
  extraClass = '';

  /**
   * Set resource location.
   *
   * @param url resource URL
   */
  @Input()
  set resource(url: string) {
    if (url === undefined) {
      url = '';
    }
    if ((this.absoluteUrl = this.isAbsoluteUrl(url))) {
      this.url = url;
    } else {
      this.initRoutingContext(url);
    }
  }

  @Input()
  isNavMenu: boolean;

  @Output()
  clicked = new EventEmitter<MouseEvent>();

  templateRef: TemplateRef<any>;

  /**
   * Linked resource URL.
   */
  url: string;
  context: any;

  /**
   * Whether URL is absolute or not.
   */
  private absoluteUrl = true;

  /**
   * Routing context.
   */
  routingContext: RoutingContext;

  targetAttr: string;

  private platformBrowser = isPlatformBrowser(this.platformId);

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private store: Store<IAppState>
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.resource) {
      if (this.anchor && this.isAbsoluteUrl(changes.resource.currentValue)) {
        this.templateRef = this.absoluteRef;
        this.url = changes.resource.currentValue;
      } else {
        if (changes.anchor && !changes.anchor.currentValue) {
          this.templateRef = this.anchoredRef;
        } else {
          this.initRoutingContext(changes.resource.currentValue);
        }
      }
    }
  }

  private initRoutingContext(url: string) {
    const urlTree = this.router.parseUrl(url);
    const { queryParams, fragment } = urlTree;

    // Set absolute URL for obfuscated links.
    this.url = this.router.serializeUrl(urlTree);

    urlTree.fragment = null;
    urlTree.queryParams = {};

    let path = this.router.serializeUrl(urlTree);

    // Special case of Homepage route.
    // Angular router expects an empty path instead of `/`.
    if (path === '/') {
      path = '';
    }
    const commands = [path];

    if (path.endsWith('/') && !this.stripSlash) {
      commands.push('');
    }
    this.templateRef = this.routerRef;

    this.routingContext = {
      commands,
      fragment,
      queryParams
    };
  }

  onClick(event: MouseEvent): void {
    if (this.isNavMenu) {
      this.resetFilteredCampaign();
    }
    if (this.stopPropagation) {
      event.stopPropagation();
    }
    this.clicked.emit(event);
  }

  navigate(event: MouseEvent): void {
    if (this.isNavMenu) {
      this.resetFilteredCampaign();
    }
    if (this.stopPropagation) {
      event.stopPropagation();
    }
    this.clicked.emit(event);

    if (!this.platformBrowser) {
      return;
    }

    if (this.absoluteUrl) {
      if (this.blank) {
        window.open(this.url);
      } else {
        document.location.replace(this.url);
      }
    } else {
      if (this.disableLink) {
        return;
      } else {
        const { commands, fragment, queryParams } = this.routingContext;
        this.router.navigate(commands, { fragment, queryParams });
      }
    }
  }

  /**
   * Checks whether URL is absolute or not.
   *
   * @returns `true` if URL is an absolute one.
   */
  private isAbsoluteUrl(url: string): boolean {
    const pattern = /^([a-z]+:)?\/\//i;
    return pattern.test(url);
  }

  /**
   * reset context(merch blockId) passed to store and filtered_campaign_id varaible in data-layer
   * on accessing the catalog using the menu
   */
  private resetFilteredCampaign() {
    this.store.dispatch(new TrackMerchBlock({ cmsBlockId: '', isMerchBlock: false }));
    this.store.dispatch(new fromDataLayer.Persist({ filtered_campaign_id: '' }));
  }
}
